import { get, post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { logger } from 'src/logger';
import { BaseballCardApiResponse, BaseballCardEmployee, FintechPapiEmployee } from './interfaces';
import { getEmployeeByLogin } from './papi-service';
import { BATCH_SIZE } from 'src/components/das-finsuite/baseball-cards/constants/BaseballCardConstants';
import { WORKDOCS_TOKEN_HEADER } from 'src/constants/WorkDocsConstants';
import { OrgChartEmployee } from 'src/components/das-finsuite/baseball-cards/types/orgChart.types';
import { transformToOrgChartEmployee } from 'src/components/das-finsuite/baseball-cards/utils/employeeTransformers';

const BATCH_DELAY_MS = 500;

/**
 * Fetches the current user's Cognito ID token (JWT) for the `x-id-token` request header.
 *
 * The Baseball-Card API is reached with SigV4 credentials from a Cognito Identity Pool role that
 * is shared by EVERY logged-in FinSuite user, so the request principal alone cannot tell the
 * backend WHICH employee is calling. Forwarding the verified ID token supplies that identity
 * (IDOR / CWE-639 remediation, Shepherd 02105f81-56ff-4fe0-a8b3-3865f15d23c0). Fetched per call
 * (not cached) so a refreshed session is used and an expired token is never sent. Returns null on
 * any failure — the request still goes out and the backend decides whether to allow it.
 */
const getCurrentIdToken = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString() ?? null;
    if (!token) {
      logger.warn('No Cognito ID token available for BASEBALL_CARD_API request');
    }
    return token;
  } catch (error) {
    logger.warn('Failed to fetch Cognito ID token for BASEBALL_CARD_API request', {
      error: error instanceof Error ? error.message : String(error)
    });
    return null;
  }
};

/**
 * Builds the identity headers for a BASEBALL_CARD_API request: `{ 'x-id-token': <jwt> }` when a
 * token is available, else `{}`. Amplify includes these headers in the SigV4 SignedHeaders.
 *
 * NOTE: do NOT rename this header to `Authorization` — Amplify treats an `authorization` header as
 * "IAM auth not applicable" and would DROP SigV4 signing, breaking the API Gateway's IAM auth.
 */
const buildIdentityHeaders = async (): Promise<Record<string, string>> => {
  const token = await getCurrentIdToken();
  return token ? { 'x-id-token': token } : {};
};

export const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export class TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TimeoutError';
  }
}

/**
 * Wraps a promise with a timeout and optional abort signal support.
 * @param promise - The promise to wrap
 * @param timeoutMs - Timeout in milliseconds
 * @param errorMsg - Error message for timeout
 * @param signal - Optional AbortSignal for cancellation
 */
export const withTimeout = <T>(promise: Promise<T>, timeoutMs: number, errorMsg: string, signal?: AbortSignal): Promise<T> => {
  let timeoutId: NodeJS.Timeout;

  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => reject(new TimeoutError(errorMsg)), timeoutMs);
  });

  // If signal is provided, add abort handling
  const abortPromise = signal
    ? new Promise<T>((_, reject) => {
        if (signal.aborted) {
          const error = new Error('Request was cancelled');
          error.name = 'AbortError';
          reject(error);
        }
        signal.addEventListener(
          'abort',
          () => {
            const error = new Error('Request was cancelled');
            error.name = 'AbortError';
            reject(error);
          },
          { once: true }
        );
      })
    : null;

  const racingPromises = [promise.finally(() => clearTimeout(timeoutId)), timeoutPromise];
  if (abortPromise) {
    racingPromises.push(abortPromise);
  }

  return Promise.race(racingPromises);
};

/**
 * Transforms a FintechPapiEmployee to a BaseballCardEmployee
 */
export const transformToBaseballCardEmployee = (employee: FintechPapiEmployee): BaseballCardEmployee => ({
  login: employee.basicInfo.login || '',
  firstName: employee.basicInfo.firstName || '',
  lastName: employee.basicInfo.lastName || '',
  jobLevel: employee.job.jobLevel || '',
  jobTitle: employee.basicInfo.businessTitle || '',
  costCenterName: employee.basicInfo.costCenterName || '',
  city: employee.workLocation.city || '',
  country: employee.workLocation.country || ''
});

export const fetchEmployeesBatch = async (aliases: string[]): Promise<BaseballCardEmployee[]> => {
  const employeeList: BaseballCardEmployee[] = [];

  for (let i = 0; i < aliases.length; i += BATCH_SIZE) {
    const batch = aliases.slice(i, i + BATCH_SIZE);

    const results = await Promise.all(
      batch.map(async (alias) => {
        try {
          const response = await getEmployeeByLogin(alias, 'job');
          return response.employee;
        } catch (error) {
          logger.warn('Failed to fetch employee', {
            alias,
            error: error instanceof Error ? error.message : String(error)
          });
          return undefined;
        }
      })
    );

    results.forEach((employee) => {
      if (employee) {
        employeeList.push(transformToBaseballCardEmployee(employee));
      }
    });

    if (i + BATCH_SIZE < aliases.length) {
      await sleep(BATCH_DELAY_MS);
    }
  }

  return employeeList;
};

/**
 * Checks if an AbortSignal has been aborted and throws if so.
 */
const checkAbortSignal = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    const error = new Error('Request was cancelled');
    error.name = 'AbortError';
    throw error;
  }
};

/**
 * Fetches employees in batches for the org chart, including direct reports data.
 * Returns a Map for efficient lookup by login.
 *
 * @param aliases - Array of employee aliases to fetch
 * @param onProgress - Optional callback to report progress (processed count)
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Map of login to OrgChartEmployee
 */
export const fetchEmployeesBatchForOrgChart = async (
  aliases: string[],
  onProgress?: (processed: number) => void,
  signal?: AbortSignal
): Promise<Map<string, OrgChartEmployee>> => {
  const employeeMap = new Map<string, OrgChartEmployee>();
  let processed = 0;

  for (let i = 0; i < aliases.length; i += BATCH_SIZE) {
    // Check if cancelled before starting each batch
    checkAbortSignal(signal);

    const batch = aliases.slice(i, i + BATCH_SIZE);

    const results = await Promise.all(
      batch.map(async (alias) => {
        try {
          // Check if cancelled before each individual request
          checkAbortSignal(signal);

          const response = await getEmployeeByLogin(alias, 'job,direct-reports', signal);
          if (response.employee) {
            const orgChartEmployee = transformToOrgChartEmployee(response.employee);
            return { alias, employee: orgChartEmployee };
          }
          return { alias, employee: undefined };
        } catch (error) {
          // Re-throw abort errors
          if (error instanceof Error && error.name === 'AbortError') {
            throw error;
          }
          logger.warn('Failed to fetch employee in batch for org chart', {
            alias,
            error: error instanceof Error ? error.message : String(error)
          });
          return { alias, employee: undefined };
        }
      })
    );

    // Check if cancelled after batch completes
    checkAbortSignal(signal);

    results.forEach(({ alias, employee }) => {
      if (employee) {
        employeeMap.set(alias, employee);
      }
    });

    processed += batch.length;
    onProgress?.(processed);

    if (i + BATCH_SIZE < aliases.length) {
      await sleep(BATCH_DELAY_MS);
    }
  }

  return employeeMap;
};

export const getBaseballCardByLogin = async (login: string): Promise<BaseballCardApiResponse | null> => {
  try {
    const path = `/v1/baseball-card/login/${login.toLowerCase()}`;

    logger.debug('Fetching baseball card', { login, path });

    // Cache-bust: the backend sends `Cache-Control: public, max-age=3600` on every
    // response, so the browser would otherwise serve a stale cached body for up to
    // an hour — a manual summary edit would not appear until the cache expired, even
    // after a full page reload. A unique query param forces a fresh network request.
    // The param is merged into the URL before SigV4 signing, so signing stays valid.
    const response = await get({
      apiName: 'BASEBALL_CARD_API',
      path,
      options: {
        queryParams: { _t: `${Date.now()}` },
        // Forward the verified end-user identity (IDOR/CWE-639 remediation). Amplify includes this
        // header in the SigV4 SignedHeaders; the backend verifies it and authorizes caller-vs-target.
        headers: await buildIdentityHeaders()
      }
    }).response;

    // Use text() to fully consume the ReadableStream before parsing
    // This prevents incomplete data issues with AWS Amplify v6
    const responseText = await response.body.text();
    const responseBody = JSON.parse(responseText) as BaseballCardApiResponse;

    return responseBody;
  } catch (error) {
    logger.warn('BASEBALL_CARD_API getBaseballCardByLogin failed', {
      error: error instanceof Error ? error.message : String(error),
      login
    });
    return null;
  }
};

// A JSON-serializable value — the shape Amplify's REST `post` body accepts. Used to
// type the `content` payload without resorting to `any`.
type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };

/**
 * Persists a merge-write for an employee's baseball card via the /update-direct
 * route: `POST /v1/baseball-card/{alias}/update-direct` with body
 * `{ sort_key, content }`. The backend performs the DynamoDB write (partition key =
 * alias lowercased, sort key = `sortKey`). Returns true on success, false on any
 * failure — callers treat a false as non-fatal.
 */
const postUpdateDirect = async (
  alias: string,
  sortKey: string,
  content: Record<string, JsonValue>,
  label: string,
  logContext: Record<string, unknown> = {}
): Promise<boolean> => {
  const path = `/v1/baseball-card/${alias.toLowerCase()}/update-direct`;
  try {
    logger.debug(`Updating baseball card via ${label}`, { alias, sortKey, path, ...logContext });

    const response = await post({
      apiName: 'BASEBALL_CARD_API',
      path,
      options: {
        body: { sort_key: sortKey, content },
        // Forward the verified end-user identity (IDOR/CWE-639 remediation); see getBaseballCardByLogin.
        headers: await buildIdentityHeaders()
      }
    }).response;

    // Consume the stream to surface any parse/transport error before returning success.
    await response.body.text();

    return true;
  } catch (error) {
    logger.warn(`BASEBALL_CARD_API ${label} failed`, {
      error: error instanceof Error ? error.message : String(error),
      alias,
      ...logContext
    });
    return false;
  }
};

/**
 * Persists the leader's root WorkDocs folder id (sort_key WORKDOCS_FOLDER_ID). The
 * value is written under the snake_case DynamoDB attribute `workdocs_folder_id`
 * inside `content`, and the GET card response serializes it camelCase as
 * `workdocsFolderId`.
 *
 * NOTE: the backend's /update-direct route allowlists sort keys — until the
 * WORKDOCS_FOLDER_ID key is allowlisted server-side this returns false (the folder
 * tree still exists; only the reference save fails). Only alias is logged.
 */
export const updateBaseballCardWorkDocsFolderId = async (alias: string, folderId: string): Promise<boolean> =>
  postUpdateDirect(alias, 'WORKDOCS_FOLDER_ID', { workdocs_folder_id: folderId }, 'updateBaseballCardWorkDocsFolderId');

/** The reporting subtree the org-card generation run should cover. */
export type OrgCardScope = 'direct-reports' | 'all-reports';

/** Parameters for {@link generateOrgCards}, in the UI's camelCase vocabulary. */
export interface GenerateOrgCardsParams {
  /** The leader whose reporting subtree is expanded server-side (path param). */
  leaderAlias: string;
  /** The user who initiated the run; receives the completion email (NOT the leader). */
  submitterAlias: string;
  /** The provisioned WorkDocs root folder id ({alias}-baseball-cards). */
  rootFolderId: string;
  /** The per-user WorkDocs OAuth access token — sent in the `X-WorkDocs-Token` header, never logged. */
  accessToken: string;
  /** 'direct-reports' limits the run to the leader's direct reports; 'all-reports' expands the full subtree. */
  scope: OrgCardScope;
  /** Logins to skip (unchecked employees). Matched case-insensitively server-side. */
  excludeAliases: string[];
  /** Input document-source categories the worker should read; omit/undefined => backend reads all. */
  categories?: string[];
}

/** 202 response from the org-card generation endpoint. */
export interface GenerateOrgCardsResponse {
  message: string;
  job_id: string;
  execution_arn: string;
  categories: string[];
  scope: string;
}

/**
 * Starts the org-wide baseball-card generation Step Functions run via
 * `POST /v1/baseball-card/{leader}/generate-org-cards`. The worker fans out per report
 * (the leader's own card is not generated), writes cards to WorkDocs, and emails the
 * submitter on completion. Returns the accepted job (with `job_id`) or `null` on failure.
 *
 * `categories` forwards the user's selected document-source subset; when undefined it is sent
 * as `null` (backend treats null/empty as "all categories").
 *
 * The WorkDocs access token is a per-user credential: it rides the `X-WorkDocs-Token` request
 * header (never the body, never a query param, never a log line) so it is not written to API
 * Gateway access logs (Shepherd d242539e, ROC14, CWE-522) — the same transport
 * `src/services/WorkDocsService.ts` uses for the WorkDocs proxy calls. The header name must be
 * in the Baseball Card API Gateway's CORS `allowHeaders`
 * (`DaSFintechBaseballServiceCDK` -> `lib/stacks/baseballCardApiStack.ts`) or the browser
 * preflight blocks this POST.
 */
export const generateOrgCards = async (params: GenerateOrgCardsParams): Promise<GenerateOrgCardsResponse | null> => {
  const { leaderAlias, submitterAlias, rootFolderId, accessToken, scope, excludeAliases, categories } = params;
  const path = `/v1/baseball-card/${leaderAlias.toLowerCase()}/generate-org-cards`;

  try {
    // Log everything EXCEPT the token/body: leader, submitter, scope, and how many were excluded.
    logger.debug('Starting org-card generation', {
      leaderAlias,
      submitterAlias,
      scope,
      excludeCount: excludeAliases.length,
      categoryCount: categories?.length ?? 0,
      path
    });

    const response = await post({
      apiName: 'BASEBALL_CARD_API',
      path,
      options: {
        body: {
          submitter_alias: submitterAlias,
          root_folder_id: rootFolderId,
          categories: categories ?? null,
          scope,
          exclude_aliases: excludeAliases
        },
        // `x-id-token` forwards the verified end-user identity (IDOR/CWE-639 remediation; see
        // getBaseballCardByLogin). `X-WorkDocs-Token` carries the WorkDocs OAuth credential the
        // worker needs. Amplify folds both into the SigV4 SignedHeaders.
        headers: { ...(await buildIdentityHeaders()), [WORKDOCS_TOKEN_HEADER]: accessToken }
      }
    }).response;

    // Fully consume the ReadableStream before parsing (Amplify v6 stream handling).
    const responseText = await response.body.text();
    const responseBody = JSON.parse(responseText) as GenerateOrgCardsResponse;

    logger.info('Org-card generation started', { leaderAlias, jobId: responseBody.job_id, scope: responseBody.scope });

    return responseBody;
  } catch (error) {
    // Never log the body or token — only the non-sensitive context.
    logger.warn('BASEBALL_CARD_API generateOrgCards failed', {
      error: error instanceof Error ? error.message : String(error),
      leaderAlias,
      submitterAlias,
      scope
    });
    return null;
  }
};
